<template>
  <DefaultCrudView
    @create-new="newJobPoolStatus"
    :breadcrumbsItems="breadcrumbs"
    :actions="crudActions"
    containerLarge
  >
    <template slot="content">
      <v-data-table
        :headers="headers"
        :items="jobPoolStatuses"
        class="elevation-1"
        :loading="loading"
        hide-default-footer
        :items-per-page="-1"
      >

        <template v-slot:item.allow_sum_sla="{ item }">
          <v-simple-checkbox
            v-model="item.allow_sum_sla"
            disabled
          ></v-simple-checkbox>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>

            <v-list dense>
              <v-list-item @click="editJobPoolStatus(item)">
                <v-list-item-icon>
                  <v-icon color="defaultIconColor">mdi-pencil-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('edit') }}</v-list-item-title>
              </v-list-item>

              <v-list-item @click="setNextStatus(item)">
                <v-list-item-icon>
                  <v-icon color="defaultIconColor">mdi-send-variant-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('next_status') }}</v-list-item-title>
              </v-list-item>

              <v-list-item @click="excludeJobPoolStatus(item)">
                <v-list-item-icon>
                  <v-icon color="defaultIconColor">mdi-delete-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('delete') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>

      <JobPoolStatusDialog
        ref="dialog"
        :jobPoolStatus="selectedJobPoolStatus"
        :jobDefinition="jobDefinition || {}"
        :pools="pools"
        :statuses="statuses"
        @saved="handleSaved"
      />

      <NextStatusDialog
        ref="nextStatusDialog"
        :jobPoolStatuses="jobPoolStatuses"
        @saved="handleNextStatusSaved"
      />
      
      <DefaultModal
        ref="deleteDialog"
        :title="$t('confirm_delete')"
        deleteModal
        @submit="confirmDelete"
      >
        <template v-slot:content>
          <v-row>
            <v-col cols="12">
              <p>{{ $t('action_cannot_be_undone') }}</p>
              <p v-if="jobPoolStatusToDelete">
                <strong>
                  {{getPoolTitle(jobPoolStatusToDelete.pool_id)}} -
                  {{getStatusTitle(jobPoolStatusToDelete.status_id)}}
                </strong>
              </p>
            </v-col>
          </v-row>
        </template>
      </DefaultModal>

    </template>
  </DefaultCrudView>
</template>

<script>
import { mapActions } from 'vuex';

import DefaultCrudView from '@/components/DefaultCrudView.vue'
import DefaultModal from '@/components/DefaultModal.vue'
import JobPoolStatusDialog from '@/components/job/JobPoolStatusDialog.vue';
import NextStatusDialog from '@/components/job/NextStatusDialog.vue';

import AppActions from '@/store/app/actions-types'
import JobActions from '@/store/job/actions-types'
import TenantActions from '@/store/core/tenant/actions-types'

export default {
  name: 'JobPoolStatus',

  components: {
    DefaultCrudView,
    DefaultModal,
    JobPoolStatusDialog,
    NextStatusDialog
  },

  data() {
    return {
      loading: false,
      tenants: [],
      jobDefinition: null,
      pools: [],
      jobPoolStatuses: [],
      statuses: [],
      selectedJobPoolStatus: null,
      jobPoolStatusToDelete: null
    }
  },

  computed: {
    breadcrumbs() {
      return [
        {
          text: this.$t('job_definitions'),
          prependIcon: 'mdi-clipboard-edit-outline',
          iconColor: 'primary',
          to: {
            name: "JobDefinitions",
            params: { appId: this.$route.params.appId }
          },
          disabled: false,
          exact: true,
          ripple: true,
        },
        {
          text: `${this.$t('job_pool_status')}: ${this.$route.query.jobTitle}`,
        },
      ]
    },

    crudActions() {
      return [
        {
          text: this.$t('create_job_pool_status'),
          eventToEmit: "create-new",
        }
      ]
    },

    headers() { 
      return [
        { text: this.$t('pool'), value: 'pool_title', sortable: true },
        { text: this.$t('status'), value: 'status_title', sortable: true },
        { text: this.$t('tenant'), value: 'tenant_title', sortable: true },
        { text: this.$t('order'), value: 'pool_status_order', sortable: true },
        { text: this.$t('sla'), value: 'sla', sortable: true },
        { text: this.$t('allow_sum_sla'), value: 'allow_sum_sla', sortable: true, width: "150px" },
        { text: this.$t('actions'), value: 'actions', sortable: false }
      ]
    },
  },

  mounted() {
    this.loadData()
    this.selectedJobPoolStatus = null
  },

  methods: {
    ...mapActions('core/tenant', [
      TenantActions.GET_ALL_TENANTS
    ]),

    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE
    ]),

    ...mapActions('job', [
      JobActions.GET_ALL_JOB_POOL_STATUSES,
      JobActions.GET_JOB_DEFINITION,
      JobActions.GET_ALL_STATUSES,
      JobActions.GET_ALL_POOLS,
      JobActions.DELETE_JOB_POOL_STATUS
    ]),
    
    async loadData() {
      this.loading = true
      try {
        const tenants = await this[TenantActions.GET_ALL_TENANTS]()
        this.tenants = tenants.items

        const jobDefiniton = await this[JobActions.GET_JOB_DEFINITION](this.$route.params.jobDefinitionId)
        this.jobDefinition = jobDefiniton

        const pools = await this[JobActions.GET_ALL_POOLS]()
        this.pools = pools.items

        const statuses = await this[JobActions.GET_ALL_STATUSES]()
        this.statuses = statuses.items

        const jobPoolStatuses = await this[JobActions.GET_ALL_JOB_POOL_STATUSES]({ 
          jobDefinitionId: this.$route.params.jobDefinitionId,
          order: 'job_pool_status_id'
         })
        this.jobPoolStatuses = this.enrichJobPoolStatusesData(jobPoolStatuses)

      } catch (error) {
        console.error('Error on loadData(). ', error)
        this.jobPoolStatuses = []
      } finally {
        this.loading = false
      }
    },

    enrichJobPoolStatusesData(jobPoolStatuses){
      return jobPoolStatuses.map(jps => {
        const pool_title = this.getPoolTitle(jps.pool_id)
        const status_title = this.getStatusTitle(jps.status_id)
        const tenant_id = this.jobDefinition.tenant_id
        const tenant_title = this.getTenantTitle(this.jobDefinition.tenant_id)
        return ({
          ...jps,
          pool_title,
          status_title,
          tenant_id,
          tenant_title,
        })
      })
    },

    getPoolTitle(id) {
      const pool = this.pools.find(f => f.pool_id == id)
      return pool ? pool.pool_title : '—'
    },

    getStatusTitle(id) {
      return this.statuses.find(status => status.status_id == id)?.status_title
    },

    getTenantTitle(id) {
      const tenant = this.tenants.find(f => f.tenant_id == id)
      return tenant ? tenant.title : '—'
    },

    newJobPoolStatus() {
      this.selectedJobPoolStatus = null
      this.$refs.dialog.openModal()
    },

    handleSaved(message) {
      this.selectedJobPoolStatus = null
      this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t(message))
      this.loadData()
    },

    editJobPoolStatus(jobPoolStatus) {
      this.selectedJobPoolStatus = jobPoolStatus
      this.$refs.dialog.openModal()
    },

    excludeJobPoolStatus(jobPoolStatus) {
      this.jobPoolStatusToDelete = jobPoolStatus
      this.$refs.deleteDialog.openModal()
    },

    confirmDelete() {
      const jobPoolStatusId = this.jobPoolStatusToDelete.job_pool_status_id
      
      this[JobActions.DELETE_JOB_POOL_STATUS](jobPoolStatusId)
        .then(() => {
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('job_pool_status_deleted'))
          this.$refs.deleteDialog.closeModal()
          this.loadData()
        })
        .catch((error) => {
          console.error('Error deleting job pool status:', error)
          this.$refs.deleteDialog.setIsSubmitting(false)
        })
        .finally(() => {
          this.jobPoolStatusToDelete = null
        })
    },

    setNextStatus(jobPoolStatus) {
      this.$refs.nextStatusDialog.open(jobPoolStatus)
    },

    handleNextStatusSaved(message) {
      this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t(message))
    },
  }
}
</script>
